/**
 * Curriculum Standards Alignment
 *
 * Maps EarthVoice features and query types to K-12 education standards.
 * Used for lesson planning, teacher dashboards, and purchasing justification.
 *
 * Standards covered:
 * - NGSS (Next Generation Science Standards) — Earth & Space Science
 * - C3 Framework (Social Studies) — Geography
 * - Common Core — Literacy in Science/Technical Subjects
 * - AP Human Geography topics
 */

export const STANDARDS = {
  // ── NGSS Earth & Space Science ────────────────────────────
  'NGSS-ESS2-2': {
    id: 'NGSS-ESS2-2',
    framework: 'NGSS',
    grade: '6-8',
    title: 'Earth\'s Systems: Processes that Shape the Earth',
    description: 'Analyze and interpret data on the distribution of fossils and rocks, continental shapes, and seafloor structures to provide evidence of past plate motions.',
    features: ['terrain', 'navigation', 'overture_layer'],
  },
  'NGSS-ESS3-3': {
    id: 'NGSS-ESS3-3',
    framework: 'NGSS',
    grade: '9-12',
    title: 'Earth & Human Activity',
    description: 'Create a computational simulation to illustrate the relationships among management of natural resources, the sustainability of human populations, and biodiversity.',
    features: ['choropleth', 'compare', 'analyze', 'h3-density'],
  },

  // ── C3 Framework (Geography) ──────────────────────────────
  'C3-D2.Geo.1': {
    id: 'C3-D2.Geo.1',
    framework: 'C3',
    grade: '3-5',
    title: 'Geographic Representations',
    description: 'Construct maps and other graphic representations of both familiar and unfamiliar places.',
    features: ['navigation', 'show', 'division_action'],
  },
  'C3-D2.Geo.2': {
    id: 'C3-D2.Geo.2',
    framework: 'C3',
    grade: '3-5',
    title: 'Location and Region',
    description: 'Use maps, satellite images, photographs, and other representations to explain relationships between the locations of places and regions.',
    features: ['navigation', 'filter', 'choropleth', 'division_action'],
  },
  'C3-D2.Geo.4': {
    id: 'C3-D2.Geo.4',
    framework: 'C3',
    grade: '6-8',
    title: 'Human-Environment Interaction',
    description: 'Explain how cultural patterns and economic decisions influence environments and the daily lives of people.',
    features: ['choropleth', 'filter', 'compare', 'h3-density'],
  },
  'C3-D2.Geo.6': {
    id: 'C3-D2.Geo.6',
    framework: 'C3',
    grade: '6-8',
    title: 'Global Interconnections',
    description: 'Explain how the relationship between the environmental characteristics of places and production of goods influences the spatial patterns of world trade.',
    features: ['compare', 'analyze', 'choropleth'],
  },
  'C3-D2.Geo.7': {
    id: 'C3-D2.Geo.7',
    framework: 'C3',
    grade: '9-12',
    title: 'Human Population: Spatial Patterns and Movements',
    description: 'Analyze the reciprocal nature of how historical events and the spatial diffusion of ideas, technologies, and cultural practices have influenced migration patterns.',
    features: ['choropleth', 'compare', 'analyze', 'filter'],
  },
  'C3-D2.Geo.8': {
    id: 'C3-D2.Geo.8',
    framework: 'C3',
    grade: '9-12',
    title: 'Global Interconnections',
    description: 'Evaluate the consequences of human-made and natural catastrophes on global trade, politics, and human migration.',
    features: ['compare', 'analyze', 'educational'],
  },

  // ── AP Human Geography ────────────────────────────────────
  'APHG-1': {
    id: 'APHG-1',
    framework: 'AP Human Geography',
    grade: '9-12',
    title: 'Unit 1: Thinking Geographically',
    description: 'Maps, spatial data, and geographic technologies (GIS, GPS, remote sensing).',
    features: ['navigation', 'show', 'terrain', 'overture_layer'],
  },
  'APHG-2': {
    id: 'APHG-2',
    framework: 'AP Human Geography',
    grade: '9-12',
    title: 'Unit 2: Population & Migration',
    description: 'Population distribution, density, composition, and migration patterns.',
    features: ['choropleth', 'compare', 'analyze', 'filter', 'h3-density'],
  },
  'APHG-3': {
    id: 'APHG-3',
    framework: 'AP Human Geography',
    grade: '9-12',
    title: 'Unit 3: Cultural Patterns & Processes',
    description: 'Cultural landscapes, diffusion, religion, language, ethnicity.',
    features: ['filter', 'division_action', 'educational'],
  },
  'APHG-4': {
    id: 'APHG-4',
    framework: 'AP Human Geography',
    grade: '9-12',
    title: 'Unit 4: Political Patterns & Processes',
    description: 'Political boundaries, sovereignty, devolution, supranational organizations.',
    features: ['division_action', 'navigation', 'show', 'educational'],
  },
  'APHG-6': {
    id: 'APHG-6',
    framework: 'AP Human Geography',
    grade: '9-12',
    title: 'Unit 6: Cities & Urban Land-Use',
    description: 'Urbanization, city models, smart growth, sustainability.',
    features: ['filter', 'h3-density', 'overture_layer', 'terrain'],
  },
};

/**
 * Get all standards aligned to a given feature/intent type.
 * @param {string} intentType - e.g., 'choropleth', 'navigation', 'filter'
 * @returns {Array} Matching standards
 */
export function getAlignedStandards(intentType) {
  return Object.values(STANDARDS).filter((s) =>
    s.features.includes(intentType)
  );
}

/**
 * Get all standards for a grade band.
 * @param {string} grade - e.g., '3-5', '6-8', '9-12'
 * @returns {Array} Matching standards
 */
export function getStandardsByGrade(grade) {
  return Object.values(STANDARDS).filter((s) => s.grade === grade);
}

/**
 * Get a human-readable standards summary for a session.
 * @param {object} featureUsage - Map of { intentType: count }
 * @returns {Array} Unique standards covered, with usage counts
 */
export function getSessionStandards(featureUsage) {
  const covered = new Map();

  for (const [type, count] of Object.entries(featureUsage)) {
    const aligned = getAlignedStandards(type);
    for (const standard of aligned) {
      if (!covered.has(standard.id)) {
        covered.set(standard.id, { ...standard, queryCount: 0 });
      }
      covered.get(standard.id).queryCount += count;
    }
  }

  return [...covered.values()].sort((a, b) => b.queryCount - a.queryCount);
}
